import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';
import type {
  CreateFeatureFlagPayload,
  FeatureFlag,
  FeatureFlagAuditEntry,
  FeatureFlagQuery,
  UpdateFeatureFlagPayload,
} from '../types';

/**
 * osprey-api `/feature-flags` (PE-2161 FE, backend PE-2160).
 *
 * TCS-staff only: the controller carries `CollectiveOnlyGuard` *and*
 * `PermissionGuard` with `@RequiresPermission(FEATURE_FLAG, READ)` at class
 * level, MANAGE on create/update/delete. Here the guard and the watchman grant
 * agree for once — watchman seeds FEATURE_FLAG:[READ, MANAGE] to
 * COLLECTIVE_SUPERADMIN and COLLECTIVE_ADMIN only, and nobody else — so the
 * frontend constants mirror both without a discrepancy to work around.
 *
 * Not tenant-scoped: the merchant is a `scopeValue`, never `x-tenant-id`, and
 * `/feature-flags` is absent from ApiFetch's TENANT_SCOPED_PREFIXES, so no
 * `tenantId` is appended — which is correct.
 *
 * `POST /feature-flags/evaluate` is deliberately not wrapped: it lives on a
 * separate controller open to any authenticated session, and nothing in Osprey
 * gates on a flag yet. A wrapper with no caller would be dead code.
 */
export const featureFlagsApi = {
  list: async (query: FeatureFlagQuery = {}): Promise<FeatureFlag[]> => {
    const search = new URLSearchParams();
    if (query.key) search.append('key', query.key);
    if (query.scope) search.append('scope', query.scope);
    // Sent as the string 'true'/'false': the server's @Transform maps exactly
    // those two literals back to booleans, so anything else fails @IsBoolean.
    if (query.enabled !== undefined) search.append('enabled', String(query.enabled));
    const qs = search.toString();
    const response = await ApiFetch.get<Enveloped<FeatureFlag[]>>(
      `/feature-flags${qs ? `?${qs}` : ''}`,
    );
    return unwrap(response);
  },

  get: async (id: string): Promise<FeatureFlag> => {
    const response = await ApiFetch.get<Enveloped<FeatureFlag>>(`/feature-flags/${id}`);
    return unwrap(response);
  },

  /** 409 when (key, scope, scopeValue) already exists — that triple is the unique index. */
  create: async (payload: CreateFeatureFlagPayload): Promise<FeatureFlag> => {
    const response = await ApiFetch.post<Enveloped<FeatureFlag>>('/feature-flags', payload);
    return unwrap(response);
  },

  /** PATCH — ApiFetch.put issues PATCH; legacy_put is the one that issues PUT. */
  update: async (id: string, payload: UpdateFeatureFlagPayload): Promise<FeatureFlag> => {
    const response = await ApiFetch.put<Enveloped<FeatureFlag>>(`/feature-flags/${id}`, payload);
    return unwrap(response);
  },

  remove: (id: string): Promise<unknown> => ApiFetch.del(`/feature-flags/${id}`),

  auditLog: async (id: string): Promise<FeatureFlagAuditEntry[]> => {
    const response = await ApiFetch.get<Enveloped<FeatureFlagAuditEntry[]>>(
      `/feature-flags/${id}/audit-log`,
    );
    return unwrap(response);
  },
};
